import articleDeliveryStatuses from "astrid-firestore/src/api/articles/constants/articleDeliveryStatuses";
import { ArticleChannel, articlePublicationStatus } from "astrid-firestore/src/api/articles/types/ArticleChannel";
import { getCollectionData } from "astrid-firestore/src/helpers";
import { formatDate } from "astrid-web/src/helpers/date";

import articleDeliveryTypes from "../../src/api/articles/constants/articleDeliveryTypes";
import articlePublicationStatuses from "../../src/api/articles/constants/articlePublicationStatuses";
import articleStatuses from "../../src/api/articles/constants/articleStatuses";
import articleTypes from "../../src/api/articles/constants/articleTypes";
import getPublicationStatus from "../../src/api/articles/getPublicationStatus";
import distributionPriceTypes from "../../src/api/distribution/constants/distributionPriceTypes";
import { channelSupportsArticle } from "../../src/api/distribution/utils/deliverArticlesToChannels";
import getPublisherDistributionPresets from "../../src/api/distribution/utils/getPublisherDistributionPresets";
import productionStatuses from "../../src/api/productions/constants/productionStatuses";

const CHANNEL_NAME_MAPPING = {
	bookbeat: "Bookbeat",
	storytel: "Storytel",
	storytelFilesDotCom: "Storytel",
	storytelOld: "Storytel",
	storytool: "Storytel",
	storytoolWav: "Storytel",
	storytoolMp3: "Storytel",
	nextory: "Nextory",
	nextory_audio: "Nextory",
	nextory_ebook: "Nextory",
	overdrive: "Overdrive",
	ellibs: "Ellibs",
	bokbasen: "Bokbasen",
	bokbasenApi: "Bokbasen",
	axiellFtp: "Axiell",
	publit: "Publit",
	bonnierWav: "Bonnier",
	bonnierMp3: "Bonnier",
	polaris: "Polaris",
	nfg: "NFG",
	bookwire: "Bookwire",
	clipsource: "Clipsource",
	kungligaBiblioteket_audio: "Kungliga Biblioteket",
	kungligaBiblioteket_ebook: "Kungliga Biblioteket",
};

const IGNORE_OLD_CHANNELS = [
	"elisa",
	"fabel",
	/*
	"storytool",
	"storytoolWav",
	"storytoolMp3",
*/
	"Landsbókasafn",
	"landsbokasafn",
	"optimal",
	"bod",
	"audibleDE",
	"audibleUK",
	"litres",
	"bookmate",
];

const CHANNEL_PRIORITY = {
	storytelFilesDotCom: 1,
	storytel: 2,
	storytelOld: 3,
	storytoolWav: 4,
	storytoolMp3: 5,
	storytool: 6,
	nextory_audio: 1,
	nextory_ebook: 1,
	nextory: 2,
};

function mergeReleaseDataByPriority(releaseData) {
	const grouped = {};

	for (const [originChannelName, data] of Object.entries(releaseData)) {
		const newChannelName = CHANNEL_NAME_MAPPING[originChannelName];
		if (IGNORE_OLD_CHANNELS.includes(originChannelName)) {
			// ignore and continue with the rest.
			continue;
		}

		if (!newChannelName) {
			throw new Error(`Unknown channel: ${originChannelName}`);
		}

		if (!grouped[newChannelName]) {
			grouped[newChannelName] = [];
		}

		grouped[newChannelName].push({
			channelKey: originChannelName,
			meta: data.meta,
			artifact: data.artifact,
			priority: CHANNEL_PRIORITY[originChannelName] || 9999,
		});
	}

	const final = {};

	for (const [channelName, entries] of Object.entries(grouped)) {
		const sorted = entries.sort((a, b) => a.priority - b.priority);

		const bestMeta = sorted.find((entry) => entry.meta?.sent) || sorted[0];
		const bestArtifact = sorted.find((entry) => entry.artifact?.sent) || sorted[0];

		final[channelName] = {
			meta: bestMeta.meta || {},
			artifact: bestArtifact.artifact || {},
		};
	}

	return final;
}

function channelSupportsMetadata(channel) {
	return !channel.acceptedDeliveryTypes?.length || channel.acceptedDeliveryTypes.includes("metadata");
}

function mapArtifactReleaseData({ production, articleType }) {
	return Object.entries(production.master?.[articleType]?.exports || {}).reduce((acc, [_, exportData]) => {
		return {
			...acc,
			...Object.entries(exportData.distributor || {}).reduce(
				(acc, [channel, distributorData]) => {
					if (
						acc?.[channel] &&
						(!distributorData.sent || acc?.[channel]?.sent?.toDate?.() > distributorData.sent?.toDate?.())
					) {
						// means we have different exports for the same distributor (wav and mp3 for example). Pick latest sent date.
						// skip info for oldest one.
						return acc;
					}
					return {
						...acc,
						[channel]: {
							sent: distributorData.sent,
							isDeliveryNeeded:
								production.master?.[articleType]?.settings?.date?.toDate?.() >
								exportData?.created?.toDate?.(),
						},
					};
				},
				{ ...acc },
			),
		};
	}, {});
}

function hasSinglePrice({ metadata }) {
	return metadata.priceUnitSale || metadata.priceOCOU;
}

function hasPayPerLoanPrice({ metadata }) {
	return metadata.pricePayPerLoan || metadata.priceCPC;
}

function mapMetadataReleaseData({ api, production, release }) {
	return Object.entries(release || {}).reduce((acc, [channel, metadata]) => {
		const regularCheck =
			metadata?.metaSent?.toDate?.() < metadata?.metaUpdated?.toDate?.() ||
			metadata?.metaSent?.toDate?.() < production.metaUpdated?.toDate?.();
		const simpleCheck = false;
		return {
			...acc,
			[channel]: {
				availability: metadata.availability ?? null,
				sent: metadata.metaSent || null,
				isDeliveryNeeded: api.deliveryNeededSimpleCheck ? simpleCheck : regularCheck,
				announcementDate: metadata.announcementDate || null,
				releaseDate: metadata.date || null,
				takedownDate: metadata.takedown || null,
				countries: metadata.countriesIncluded || (metadata.world ? null : metadata.markets),
				prices:
					hasSinglePrice({ metadata }) || hasPayPerLoanPrice({ metadata })
						? [
								hasSinglePrice({ metadata }) && {
									amount: Number(metadata.priceUnitSale || metadata.priceOCOU),
									currency:
										metadata.priceUnitSaleCurrency ||
										metadata.priceOCOUCurrency ||
										(channel === "ellibs" ? "EUR" : "SEK"),
									type: distributionPriceTypes.SINGLE,
								},
								hasPayPerLoanPrice({ metadata }) && {
									amount: Number(metadata.pricePayPerLoan || metadata.priceCPC),
									currency: metadata.pricePayPerLoanCurrency || metadata.priceCPCCurrency || "SEK",
									type: distributionPriceTypes.PAY_PER_LOAN,
								},
						  ].filter(Boolean)
						: null,
			},
		};
	}, {});
}

function combineReleaseData({ meta, artifact }) {
	const combined = {};

	const platforms = new Set([...Object.keys(meta || {}), ...Object.keys(artifact)]);

	platforms.forEach((platform) => {
		combined[platform] = {
			meta: meta[platform] || {},
			artifact: artifact[platform] || {},
		};
	});

	return combined;
}

function getReleaseData({ api, production, articleType }) {
	if (articleType === "total") {
		const meta = mapMetadataReleaseData({ api, production, release: production.release });
		const artifact = mapArtifactReleaseData({ api, production, articleType });
		return combineReleaseData({ meta, artifact });
	} else if (articleType.includes("part")) {
		const partNumber = articleType.split("_")[1];
		const meta = mapMetadataReleaseData({
			api,
			production,
			release: production.deliveryParts[partNumber].release,
		});
		const artifact = mapArtifactReleaseData({ api, production, articleType });
		return combineReleaseData({ meta, artifact });
	} else if (articleType === "ebook") {
		const meta = mapMetadataReleaseData({ api, production, release: production.deliveryEbook.release });
		// only take metadata status if there exists an ebook
		const artifact = production.deliveryEbook?.path ? meta : {};
		return combineReleaseData({ meta, artifact });
	} else {
		throw new Error(`Unknown article type when fetching release data: ${articleType}`);
	}
}

function disableChannel({ meta, artifact, mergedReleaseData, isNewPresetChannel }) {
	if (meta?.availability === false) {
		// disable titles that have been taken down.
		return true;
	}

	if (isNewPresetChannel) {
		// new preset channel should be enabled even if we have not sent anything unless production is done.
		return false;
	}

	const hasNotSentToChannel = !meta.sent && !artifact.sent;
	const haveSentToOtherChannel = mergedReleaseData.some(({ meta, artifact }) => meta?.sent || artifact?.sent);

	if (hasNotSentToChannel && haveSentToOtherChannel) {
		return true;
	}

	return false;
}

function getLatestReleaseDate({ api }, { mergedReleaseData }) {
	const releaseDates = mergedReleaseData
		.map(({ meta }) => {
			return meta.releaseDate && api.Timestamp(meta?.releaseDate)?.toDate?.();
		})
		.filter(Boolean)
		.sort((a, b) => b - a);
	return releaseDates.length > 0 ? formatDate(releaseDates[0]) : null;
}

async function checkComputeJobs({ api, env }, { production, articleType, releaseData }) {
	if (
		articleType !== articleTypes.EBOOK &&
		production.status === productionStatuses.DONE && // only check compute jobs if production is done.
		Object.values(releaseData)?.some((channelReleaseData) => !channelReleaseData?.artifact?.sent) // some channel has missing data.
	) {
		const computeJobsForArticle = await getCollectionData(
			api[env].db.collection("tasks/compute/jobs").orderBy(`args.articles.${production.id}.${articleType}`),
		);
		// console.log("checked compute jobs for article", articleType, computeJobsForArticle.length);
		const withComputeJobInfo = Object.entries(releaseData).map(([channelName, channelReleaseData]) => {
			if (!channelReleaseData?.artifact?.sent) {
				const latestArtifactDelivery = computeJobsForArticle
					.filter((job) => {
						return (
							job.args.distributor === channelName &&
							job?.args?.articles?.[production.id]?.[articleType] !== "metadata" &&
							job.status === "done"
						);
					})
					.sort((a, b) => b?.done?.toDate?.() - a?.done?.toDate?.())?.[0];

				if (latestArtifactDelivery) {
					return {
						[channelName]: {
							...channelReleaseData,
							artifact: {
								sent: latestArtifactDelivery.done,
							},
						},
					};
				}
			}
			return {
				[channelName]: channelReleaseData,
			};
		});

		return withComputeJobInfo.reduce((acc, channelData) => {
			return {
				...acc,
				...channelData,
			};
		}, {});
	}
	return releaseData;
}

export default async function createChannels({ api, env }, { article, fullPublisherDoc, production, articleType }) {
	if (article.status === articleStatuses.DRAFT) {
		return { channels: [], channelIds: [] };
	}
	const distributionChannels = api.convertFromLiveToStage
		? await getCollectionData(api.stage.db.collection("distributionChannels"))
		: await getCollectionData(api[env].db.collection("distributionChannels"));
	const releaseData = getReleaseData({ api, production, articleType });
	const addReleaseInfoFromComputeJobs = await checkComputeJobs(
		{ api, env },
		{ production, articleType, releaseData },
	);

	const mergedReleaseDataByPriority = mergeReleaseDataByPriority(addReleaseInfoFromComputeJobs);

	const mappedToNewChannelNames = Object.entries(mergedReleaseDataByPriority).map(([channelName, releaseData]) => {
		const { meta, artifact } = releaseData;
		return {
			channelName,
			meta,
			artifact,
		};
	});

	const presets = getPublisherDistributionPresets({ article, publisher: fullPublisherDoc }).map((preset) => {
		return {
			channelId: preset.channel.id,
			channelName: preset.channel.name,
			meta: {
				availability: production.status === productionStatuses.DONE ? false : true, // disable presets that are added for finished productions (for example podme since it is a new channel)
			},
			deliveryTypes: preset.deliveryTypes || [articleDeliveryTypes.METADATA, articleDeliveryTypes.ARTIFACT],
			targetData: preset.targetData,
			isNewPresetChannel: true,
		};
	});

	const presetChannelIds = presets.map((preset) => preset.channelId);

	const merged = [
		...mappedToNewChannelNames,
		...presets.filter(
			(preset) => !mappedToNewChannelNames.some((mappedItem) => mappedItem.channelName === preset.channelName),
		),
	];

	const latestReleaseDate = getLatestReleaseDate({ api }, { mergedReleaseData: merged });

	return merged.reduce(
		(acc, { channelName, meta, artifact, isNewPresetChannel }) => {
			const channel = distributionChannels.find((channel) => channel.name === channelName);
			const channelId = channel?.id;
			const preset = presets.find((preset) => preset.channelId === channelId);
			const targetData = presets?.targetData;

			if (!channelId) {
				// means we have not created the distribution channel yet.
				// console.warn(`Channel ${channelName} not found in distributionChannels`);
				return acc;
			}

			if (!channelSupportsArticle({ article, channel }) || !presetChannelIds.includes(channelId)) {
				return acc;
			}

			const supportsMetadata = channelSupportsMetadata(channel);

			const delivery = {
				artifact: {
					status:
						!artifact?.isDeliveryNeeded && artifact?.sent?.seconds
							? articleDeliveryStatuses.DELIVERY_COMPLETED
							: api.isFinalizing || artifact?.isDeliveryNeeded
							? articleDeliveryStatuses.DELIVERY_NEEDED
							: articleDeliveryStatuses.DELIVERY_NOT_STARTED,
					sent: artifact?.sent?.seconds ? api.Timestamp(artifact.sent) : null,
				},
				metadata: {
					status:
						supportsMetadata && !meta?.isDeliveryNeeded && meta?.sent?.seconds
							? articleDeliveryStatuses.DELIVERY_COMPLETED
							: supportsMetadata
							? api.isFinalizing || meta?.isDeliveryNeeded
								? articleDeliveryStatuses.DELIVERY_NEEDED
								: articleDeliveryStatuses.DELIVERY_NOT_STARTED
							: articleDeliveryStatuses.DELIVERY_UNSUPPORTED,
					sent: meta?.sent?.seconds ? api.Timestamp(meta.sent) : null,
				},
			};

			const announcementDate = meta?.announcementDate
				? formatDate(api.Timestamp(meta?.announcementDate)?.toDate())
				: null;
			const releaseDate = meta?.releaseDate
				? formatDate(api.Timestamp(meta.releaseDate)?.toDate())
				: latestReleaseDate;
			const takedownDate = meta?.takedownDate ? formatDate(api.Timestamp(meta.takedownDate)?.toDate()) : null;

			const channelIds = disableChannel({ meta, artifact, mergedReleaseData: merged, isNewPresetChannel })
				? acc.channelIds
				: acc?.channelIds.concat(channelId);

			return {
				...acc,
				channels: {
					...acc?.channels,
					[channelId]: ArticleChannel.parse({
						delivery,
						deliveryTypes: preset.deliveryTypes,
						announcementDate,
						releaseDate,
						takedownDate,
						publicationStatus:
							meta.availability === false && preset.deliveryTypes.includes("metadata")
								? articlePublicationStatuses.UNPUBLISHED
								: getPublicationStatus({
										article: {
											channels: {
												[channelId]: {
													delivery,
													announcementDate,
													releaseDate,
													takedownDate,
													deliveryTypes: channel?.deliveryTypes,
												},
											},
											channelIds,
										},
										channelId,
								  }),
						countries: meta?.countries || targetData?.countries || [],
						prices: meta?.prices || targetData?.prices || [],
						subChannels: targetData?.subChannels || [],
					}),
				},
				channelIds,
			};
		},
		{ channels: {}, channelIds: [] },
	);
}
